import React, { useEffect, useState } from 'react';
import {
    Box, Button, IconButton, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Typography, Pagination, Chip, Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import { format } from 'date-fns';
import { NoticeService } from '../../services/notice.service';
import { INotice } from '../../interfaces/notice.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import NoticeForm from './NoticeForm';
import NoticeReadersDialog from './NoticeReadersDialog';

const PAGE_SIZE = 10;

const NoticeAdminList: React.FC = () => {
    const toast = useToast();
    const { t } = useTranslation(['notice', 'common']);
    const institutionId = AuthService.getSelectedInstitution()!;
    const [notices, setNotices] = useState<INotice[]>([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<INotice | null>(null);
    const [readersOpen, setReadersOpen] = useState(false);
    const [readersNoticeId, setReadersNoticeId] = useState<string | null>(null);

    const fetchList = async (p: number) => {
        setLoading(true);
        try {
            const data = await NoticeService.listForAdmin(institutionId, p, PAGE_SIZE);
            setNotices(data.content);
            setTotalPages(data.totalPages);
        } catch (err) {
            toast.showApiError(err, t('noticeAdminList.errors.load'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList(page);
    }, [page]);

    const openCreate = () => { setEditing(null); setFormOpen(true); };
    const openEdit = (n: INotice) => { setEditing(n); setFormOpen(true); };

    const handleDelete = async (n: INotice) => {
        if (!window.confirm(t('noticeAdminList.confirmDelete', { title: n.title }))) return;
        try {
            await NoticeService.delete(n.id, institutionId);
            toast.showSuccess(t('noticeAdminList.deleted'));
            fetchList(page);
        } catch (err) {
            toast.showApiError(err, t('noticeAdminList.errors.delete'));
        }
    };

    const openReaders = (n: INotice) => { setReadersNoticeId(n.id); setReadersOpen(true); };

    const isExpired = (n: INotice) => new Date(n.expiresAt).getTime() < Date.now();

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">{t('noticeAdminList.title')}</Typography>
                <Button variant="contained" onClick={openCreate}>{t('noticeAdminList.newNotice')}</Button>
            </Box>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('noticeAdminList.columns.title')}</TableCell>
                            <TableCell>{t('noticeAdminList.columns.audience')}</TableCell>
                            <TableCell>{t('noticeAdminList.columns.published')}</TableCell>
                            <TableCell>{t('noticeAdminList.columns.expires')}</TableCell>
                            <TableCell>{t('noticeAdminList.columns.reads')}</TableCell>
                            <TableCell>{t('noticeAdminList.columns.status')}</TableCell>
                            <TableCell>{t('noticeAdminList.columns.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={7}><Typography>{t('loading')}</Typography></TableCell></TableRow>
                        ) : notices.length === 0 ? (
                            <TableRow><TableCell colSpan={7}><Typography color="text.secondary">{t('noticeAdminList.empty')}</Typography></TableCell></TableRow>
                        ) : notices.map(n => (
                            <TableRow key={n.id}>
                                <TableCell>{n.title}</TableCell>
                                <TableCell>
                                    {n.audienceType === 'ALL'
                                        ? t('noticeAdminList.allEmployees')
                                        : (n.departmentNames.length ? n.departmentNames.join(', ') : t('noticeAdminList.departments'))}
                                </TableCell>
                                <TableCell>{format(new Date(n.publishAt), 'dd MMM yyyy HH:mm')}</TableCell>
                                <TableCell>{format(new Date(n.expiresAt), 'dd MMM yyyy HH:mm')}</TableCell>
                                <TableCell>{n.readCount ?? 0}</TableCell>
                                <TableCell>
                                    {isExpired(n) ? <Chip label={t('noticeAdminList.expired')} size="small" /> : <Chip label={t('common:status.active')} color="success" size="small" />}
                                </TableCell>
                                <TableCell>
                                    <Tooltip title={t('noticeAdminList.viewReaders')}>
                                        <IconButton onClick={() => openReaders(n)}><PeopleIcon /></IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('common:actions.edit')}>
                                        <IconButton onClick={() => openEdit(n)} color="primary"><EditIcon /></IconButton>
                                    </Tooltip>
                                    <Tooltip title={t('common:actions.delete')}>
                                        <IconButton onClick={() => handleDelete(n)} color="error"><DeleteIcon /></IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination count={totalPages} page={page + 1} onChange={(_, p) => setPage(p - 1)} />
                </Box>
            )}
            <NoticeForm
                open={formOpen}
                notice={editing}
                onClose={() => setFormOpen(false)}
                onSaved={() => fetchList(page)}
            />
            <NoticeReadersDialog
                open={readersOpen}
                noticeId={readersNoticeId}
                onClose={() => setReadersOpen(false)}
            />
        </Box>
    );
};

export default NoticeAdminList;
